import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  Download,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Key,
  Users,
  Shield,
  HelpCircle,
  AlertTriangle,
} from "lucide-react";

export default function ImportPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [apiToken, setApiToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [importing, setImporting] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await apiClient.get("/import/logs");
      setLogs(res.data);
      
      const runningImport = res.data.find(log => log.status === "running");
      if (runningImport) {
        setTimeout(fetchLogs, 3000);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleVerifyToken = async () => {
    if (!apiToken.trim()) {
      toast.error("Bitte geben Sie einen API-Token ein");
      return;
    }

    setVerifying(true);
    setTokenValid(null);
    try {
      await apiClient.post("/import/verify-token", { api_token: apiToken });
      setTokenValid(true);
      toast.success("Token ist gültig!");
    } catch (error) {
      setTokenValid(false);
      const message = error.response?.data?.detail || "Token-Verifizierung fehlgeschlagen";
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  const handleStartImport = async () => {
    if (!tokenValid) {
      toast.error("Bitte verifizieren Sie zuerst den Token");
      return;
    }

    setImporting(true);
    try {
      await apiClient.post("/import/start", { api_token: apiToken });
      toast.success("Import gestartet!");
      setShowImportModal(false);
      setApiToken("");
      setTokenValid(null);
      fetchLogs();
    } catch (error) {
      const message = error.response?.data?.detail || "Import konnte nicht gestartet werden";
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Läuft
          </Badge>
        );
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Abgeschlossen
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            Fehlgeschlagen
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasRunningImport = logs.some(log => log.status === "running");

  return (
    <Layout title="Import">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-testid="import-header">
        <div>
          <p className="text-slate-500">
            Importieren Sie Kunden aus dem Makrolog-System
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchLogs}
            disabled={loading}
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button
            onClick={() => setShowImportModal(true)}
            disabled={hasRunningImport}
            className="bg-indigo-600 hover:bg-indigo-700 shadow-sm"
            data-testid="start-import-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Import starten
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {logs.length > 0 && logs[0].status !== "running" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription>Letzter Import</CardDescription>
              <CardTitle className="text-lg">{logs[0]?.new_customers_created || 0} neue Kunden</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-blue-500" /> SafetyAssist
              </CardDescription>
              <CardTitle className="text-lg text-blue-600">{logs[0]?.safety_assist_count || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Users className="w-3 h-3 text-purple-500" /> PresenceAssist
              </CardDescription>
              <CardTitle className="text-lg text-purple-600">{logs[0]?.presence_assist_count || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="w-3 h-3 text-slate-500" /> Unknown
              </CardDescription>
              <CardTitle className="text-lg text-slate-600">{logs[0]?.unknown_count || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Running Import Status */}
      {hasRunningImport && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <div>
                <CardTitle className="text-lg text-blue-900">Import läuft...</CardTitle>
                <CardDescription>
                  Gestartet von {logs.find(l => l.status === "running")?.triggered_by} am{" "}
                  {formatDate(logs.find(l => l.status === "running")?.started_at)}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Import Logs Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="import-logs-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Gestartet</TableHead>
              <TableHead className="font-semibold text-slate-600">Beendet</TableHead>
              <TableHead className="font-semibold text-slate-600">Ausgelöst von</TableHead>
              <TableHead className="font-semibold text-slate-600">Status</TableHead>
              <TableHead className="font-semibold text-slate-600">Gefunden</TableHead>
              <TableHead className="font-semibold text-slate-600">Neu</TableHead>
              <TableHead className="font-semibold text-slate-600">
                <span className="text-blue-600">SA</span> / <span className="text-purple-600">PA</span> / <span className="text-slate-500">UK</span>
              </TableHead>
              <TableHead className="font-semibold text-slate-600">Übersprungen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Download className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Noch keine Imports durchgeführt</p>
                  <Button
                    variant="link"
                    onClick={() => setShowImportModal(true)}
                    className="text-indigo-600 mt-2"
                  >
                    Ersten Import starten
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow
                  key={log.id}
                  className="hover:bg-slate-50/50 transition-colors"
                  data-testid={`import-log-${log.id}`}
                >
                  <TableCell className="text-slate-600">
                    {formatDate(log.started_at)}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {log.completed_at ? formatDate(log.completed_at) : "-"}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {log.triggered_by}
                  </TableCell>
                  <TableCell>{getStatusBadge(log.status)}</TableCell>
                  <TableCell className="text-slate-600">
                    {log.total_users_found}
                  </TableCell>
                  <TableCell className="font-medium text-green-600">
                    {log.new_customers_created}
                  </TableCell>
                  <TableCell>
                    <span className="text-blue-600">{log.safety_assist_count}</span>
                    {" / "}
                    <span className="text-purple-600">{log.presence_assist_count}</span>
                    {" / "}
                    <span className="text-slate-500">{log.unknown_count}</span>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {log.skipped_guest_count}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Error Display */}
      {logs.length > 0 && logs[0].status === "failed" && logs[0].error_message && (
        <Card className="mt-4 border-red-200 bg-red-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <CardTitle className="text-sm text-red-900">Letzter Fehler</CardTitle>
                <CardDescription className="text-red-700">
                  {logs[0].error_message}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Import Modal - Fixed Overflow */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg overflow-hidden" data-testid="import-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Download className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <span>Import starten</span>
            </DialogTitle>
            <DialogDescription>
              Geben Sie den API-Token für das Makrolog-System ein
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-hidden">
            <div className="space-y-2">
              <Label htmlFor="api-token" className="flex items-center gap-2">
                <Key className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span>API-Token (X-API-Token)</span>
              </Label>
              <Textarea
                id="api-token"
                value={apiToken}
                onChange={(e) => {
                  setApiToken(e.target.value);
                  setTokenValid(null);
                }}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                className="font-mono text-xs resize-none break-all"
                rows={4}
                data-testid="api-token-input"
              />
              <p className="text-xs text-slate-400">Token wird sicher übertragen und nicht gespeichert</p>
            </div>

            {/* Token Status */}
            {tokenValid !== null && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg ${
                  tokenValid ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                }`}
              >
                {tokenValid ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">Token ist gültig und bereit für den Import</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">Token ist ungültig oder abgelaufen</span>
                  </>
                )}
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700">Was passiert beim Import?</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Neue Kunden werden mit Tag "Neu" markiert</li>
                <li>• Typ wird automatisch erkannt (SafetyAssist/PresenceAssist)</li>
                <li>• Guest-E-Mails werden übersprungen</li>
                <li>• Bereits importierte UUIDs werden ignoriert</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(false)} className="w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button
              variant="outline"
              onClick={handleVerifyToken}
              disabled={verifying || !apiToken.trim()}
              className="w-full sm:w-auto"
              data-testid="verify-token-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Prüfen...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Token prüfen
                </>
              )}
            </Button>
            <Button
              onClick={handleStartImport}
              disabled={importing || !tokenValid}
              className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starten...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Import starten
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
